import api from './api';

interface GoogleDriveAuthResponse {
  authorizationUrl: string;
}

interface GoogleDriveConnectionStatus {
  connected: boolean;
  user?: string;
  email?: string;
  error?: string;
}

interface GoogleDriveFolder {
  id: string;
  name: string;
}

interface GoogleDriveSyncResult {
  message: string;
  stats: {
    filesProcessed: number;
    filesCreated: number;
    filesUpdated: number;
    filesDeleted: number;
    filesFailed: number;
    errors: string[];
  };
}

export const googleDriveApi = {
  initiateConnection: async (organizationId: string): Promise<GoogleDriveAuthResponse> => {
    if (!organizationId) {
      throw new Error('Organization ID is required but not provided.');
    }
    const response = await api.post('/api/integrations/google-drive/connect', {
      organizationId,
    });
    return response.data;
  },

  getConnectionStatus: async (dataSourceId: string): Promise<GoogleDriveConnectionStatus> => {
    const response = await api.get(`/api/integrations/google-drive/status/${dataSourceId}`);
    return response.data;
  },

  disconnect: async (dataSourceId: string): Promise<void> => {
    await api.post(`/api/integrations/google-drive/disconnect/${dataSourceId}`, {});
  },

  listSharedDrives: async (dataSourceId: string): Promise<{ drives: GoogleDriveFolder[] }> => {
    const response = await api.get(
      `/api/integrations/google-drive/${dataSourceId}/shared-drives`,
    );
    return response.data;
  },

  listFolders: async (dataSourceId: string, parentId?: string, driveId?: string): Promise<{ folders: GoogleDriveFolder[] }> => {
    const params = new URLSearchParams();
    if (parentId) params.set('parentId', parentId);
    if (driveId) params.set('driveId', driveId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(
      `/api/integrations/google-drive/${dataSourceId}/folders${qs}`,
    );
    return response.data;
  },

  selectFolder: async (dataSourceId: string, folderId: string, folderName: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/api/integrations/google-drive/${dataSourceId}/select-folder`,
      { folderId, folderName },
    );
    return response.data;
  },

  syncNow: async (dataSourceId: string): Promise<GoogleDriveSyncResult> => {
    const response = await api.post(`/api/integrations/google-drive/sync/${dataSourceId}`, {});
    return response.data;
  },
};
